import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import {
  Map as GoogleMap,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Link } from 'react-router-dom';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';

interface MapBounds {
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
}

interface MapVenue {
  _id: string;
  name: string;
  venueType: string;
  address?: {
    city?: string;
    state?: string;
    coordinates?: { lat: number; lng: number } | null;
  };
  priceRange?: number | null;
  firstHeroImage?: { cloudinaryUrl: string } | null;
  logoImage?: { cloudinaryPublicId: string; cloudinaryUrl: string } | null;
}

interface MapPanelProps {
  venues: MapVenue[];
  onBoundsChanged: (bounds: MapBounds) => void;
  highlightedVenueId: string | null;
  onPinClick: (venueId: string) => void;
  center?: { lat: number; lng: number };
  zoom?: number;
  topCenterOverlay?: ReactNode;
  topRightOverlay?: ReactNode;
  /** Override the destination URL when a user clicks an info-window pin.
   *  Defaults to `/wineries/${venueId}` so existing call sites are unchanged. */
  infoWindowLinkTo?: (venueId: string) => string;
}

// Minimal type for the Google Maps instance returned by useMap()
// (google.maps.Map is not in scope because tsconfig "types" restricts it)
interface GMapInstance {
  panTo: (latLng: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  getBounds: () => {
    getNorthEast: () => { lat: () => number; lng: () => number };
    getSouthWest: () => { lat: () => number; lng: () => number };
  } | undefined;
}

const DEFAULT_CENTER = { lat: 38.5, lng: -121.5 };
const DEFAULT_ZOOM = 8;

// --- Imperative marker helpers (inline styles since these are outside React's DOM) ---

const createPinContent = (isHighlighted: boolean): HTMLElement => {
  // Outer wrapper positions the pin with its tip at the coordinate
  const wrapper = document.createElement('div');
  wrapper.style.position = 'relative';
  wrapper.style.width = '28px';
  wrapper.style.height = '36px';
  wrapper.style.cursor = 'pointer';
  wrapper.style.transform = isHighlighted ? 'scale(1.2)' : 'scale(1)';
  wrapper.style.transition = 'transform 0.2s';

  // Pin body (teardrop shape via border-radius)
  const pin = document.createElement('div');
  pin.style.width = '28px';
  pin.style.height = '28px';
  pin.style.borderRadius = '50% 50% 50% 0';
  pin.style.transform = 'rotate(-45deg)';
  pin.style.backgroundColor = isHighlighted ? '#7e22ce' : '#9333ea';
  pin.style.border = '2px solid white';
  pin.style.boxShadow = '0 2px 6px rgba(0,0,0,0.35)';

  // Wine glass icon inside
  const icon = document.createElement('div');
  icon.style.transform = 'rotate(45deg)';
  icon.style.width = '100%';
  icon.style.height = '100%';
  icon.style.display = 'flex';
  icon.style.alignItems = 'center';
  icon.style.justifyContent = 'center';
  icon.style.fontSize = '14px';
  icon.textContent = '🍷';
  pin.appendChild(icon);

  wrapper.appendChild(pin);
  return wrapper;
};

const createClusterContent = (count: number): HTMLElement => {
  const div = document.createElement('div');
  const size = count >= 100 ? 48 : count >= 10 ? 40 : 32;
  div.style.width = `${size}px`;
  div.style.height = `${size}px`;
  div.style.borderRadius = '50%';
  div.style.backgroundColor = '#9333ea';
  div.style.color = 'white';
  div.style.fontWeight = '600';
  div.style.fontSize = count >= 100 ? '14px' : '12px';
  div.style.display = 'flex';
  div.style.alignItems = 'center';
  div.style.justifyContent = 'center';
  div.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
  div.style.border = '2px solid white';
  div.textContent = count >= 1000 ? `${Math.round(count / 100) / 10}k` : String(count);
  return div;
};

// --- Clustered markers component (imperative marker management) ---

// google.maps.marker.AdvancedMarkerElement is available at runtime but not in
// the installed @types/google.maps. We access it dynamically and use a minimal
// local type so the rest of the code stays type-safe.
interface AdvancedMarkerLike {
  position: { lat: number; lng: number } | null;
  content: HTMLElement | null;
  title: string | null;
  map: unknown;
  addListener: (event: string, handler: () => void) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAdvancedMarkerClass = (): (new (opts: Record<string, unknown>) => AdvancedMarkerLike) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (google.maps as any).marker.AdvancedMarkerElement;
};

interface ClusteredMarkersProps {
  venues: MapVenue[];
  highlightedVenueId: string | null;
  onMarkerClick: (venueId: string) => void;
}

const ClusteredMarkers = ({ venues, highlightedVenueId, onMarkerClick }: ClusteredMarkersProps) => {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<Map<string, AdvancedMarkerLike>>(new Map());

  // Initialize clusterer once map is ready
  useEffect(() => {
    if (!map) return;

    const AdvancedMarker = getAdvancedMarkerClass();

    clustererRef.current = new MarkerClusterer({
      map: map as unknown as google.maps.Map,
      markers: [],
      onClusterClick: (_event, cluster, gMap) => {
        // Zoom to fit the cluster's markers instead of default behavior
        const bounds = new google.maps.LatLngBounds();
        for (const marker of cluster.markers ?? []) {
          const pos = (marker as unknown as AdvancedMarkerLike).position;
          if (pos) bounds.extend(new google.maps.LatLng(pos.lat, pos.lng));
        }
        (gMap as unknown as google.maps.Map).fitBounds(bounds, 50);
      },
      renderer: {
        render: ({ count, position }) => {
          return new AdvancedMarker({
            position,
            content: createClusterContent(count),
          }) as unknown as google.maps.Marker;
        },
      },
    });

    return () => {
      clustererRef.current?.clearMarkers();
      clustererRef.current = null;
    };
  }, [map]);

  // Sync markers with venues
  useEffect(() => {
    if (!map || !clustererRef.current) return;

    const AdvancedMarker = getAdvancedMarkerClass();
    const clusterer = clustererRef.current;
    const oldMarkers = markersRef.current;
    const newMarkers = new Map<string, AdvancedMarkerLike>();

    const mappable = venues.filter(
      (v) => v.address?.coordinates?.lat != null && v.address?.coordinates?.lng != null
    );

    for (const venue of mappable) {
      const existing = oldMarkers.get(venue._id);
      if (existing) {
        // Update highlight state on existing marker
        existing.content = createPinContent(venue._id === highlightedVenueId);
        newMarkers.set(venue._id, existing);
        oldMarkers.delete(venue._id);
      } else {
        const marker = new AdvancedMarker({
          position: venue.address!.coordinates!,
          title: venue.name,
          content: createPinContent(venue._id === highlightedVenueId),
        });
        marker.addListener('click', () => onMarkerClick(venue._id));
        newMarkers.set(venue._id, marker);
      }
    }

    // Remove stale markers
    for (const [, marker] of oldMarkers) {
      marker.map = null;
    }

    markersRef.current = newMarkers;
    clusterer.clearMarkers();
    clusterer.addMarkers(Array.from(newMarkers.values()) as unknown as google.maps.Marker[]);
  }, [map, venues, highlightedVenueId, onMarkerClick]);

  return null;
};

// --- Main MapPanel component ---

export const MapPanel = ({
  venues,
  onBoundsChanged,
  highlightedVenueId,
  onPinClick,
  center,
  zoom,
  topCenterOverlay,
  topRightOverlay,
  infoWindowLinkTo,
}: MapPanelProps) => {
  const map = useMap();
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const isInitialBoundsRef = useRef(true);

  // Center map when center/zoom props change (e.g. region selection)
  useEffect(() => {
    if (!map || !center) return;
    const gMap = map as unknown as GMapInstance;
    gMap.panTo(center);
    if (zoom) gMap.setZoom(zoom);
  }, [map, center, zoom]);

  const handleBoundsChanged = useCallback(() => {
    if (!map) return;

    // Skip the very first bounds event (initial load) so we don't override
    // the region/location-based search that's already running
    if (isInitialBoundsRef.current) {
      isInitialBoundsRef.current = false;
      return;
    }

    const gMap = map as unknown as GMapInstance;
    const bounds = gMap.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    onBoundsChanged({
      ne: { lat: ne.lat(), lng: ne.lng() },
      sw: { lat: sw.lat(), lng: sw.lng() },
    });
  }, [map, onBoundsChanged]);

  // Reset initial bounds flag when center changes (region switch)
  useEffect(() => {
    isInitialBoundsRef.current = true;
  }, [center]);

  const selectedVenue = selectedVenueId
    ? venues.find((v) => v._id === selectedVenueId)
    : null;

  const handleMarkerClick = useCallback(
    (venueId: string) => {
      setSelectedVenueId(venueId);
      onPinClick(venueId);
    },
    [onPinClick]
  );

  return (
    <div className="w-full h-full relative">
      <GoogleMap
        defaultCenter={center || DEFAULT_CENTER}
        defaultZoom={zoom || DEFAULT_ZOOM}
        mapId="search-map-panel"
        gestureHandling="greedy"
        className="w-full h-full"
        onBoundsChanged={handleBoundsChanged}
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
      >
        <ClusteredMarkers
          venues={venues}
          highlightedVenueId={highlightedVenueId}
          onMarkerClick={handleMarkerClick}
        />

        {selectedVenue && selectedVenue.address?.coordinates && (
          <VenueInfoWindow
            venue={selectedVenue}
            onClose={() => setSelectedVenueId(null)}
            infoWindowLinkTo={infoWindowLinkTo}
          />
        )}
      </GoogleMap>
      {topCenterOverlay && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-auto">
          {topCenterOverlay}
        </div>
      )}
      {topRightOverlay && (
        <div className="absolute top-4 right-4 z-10 pointer-events-auto">
          {topRightOverlay}
        </div>
      )}
    </div>
  );
};

// --- Info window component ---

interface VenueInfoWindowProps {
  venue: MapVenue;
  onClose: () => void;
  infoWindowLinkTo?: (venueId: string) => string;
}

const VenueInfoWindow = ({ venue, onClose, infoWindowLinkTo }: VenueInfoWindowProps) => {
  if (!venue.address?.coordinates) return null;

  const thumbnailUrl =
    venue.firstHeroImage?.cloudinaryUrl ||
    venue.logoImage?.cloudinaryUrl ||
    null;

  const priceLabel = venue.priceRange ? '$'.repeat(venue.priceRange) : null;

  return (
    <InfoWindow
      position={venue.address.coordinates}
      onCloseClick={onClose}
      maxWidth={260}
    >
      <Link
        to={infoWindowLinkTo ? infoWindowLinkTo(venue._id) : `/wineries/${venue._id}`}
        className="block p-1 no-underline text-inherit"
      >
        {thumbnailUrl && (
          <div className="w-full mb-2 rounded overflow-hidden">
            <ImgWithSkeleton
              src={thumbnailUrl}
              alt={venue.name}
              fallbackSrc="/assets/placeholders/placeholder-winery-logo.png"
              className="w-full h-28 object-cover"
              skeletonClassName="rounded"
            />
          </div>
        )}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight">
            {venue.name}
          </h3>
          {priceLabel && (
            <span className="flex-shrink-0 text-xs font-medium text-green-700">
              {priceLabel}
            </span>
          )}
        </div>
        {venue.address?.city && venue.address?.state && (
          <p className="text-xs text-gray-500 mt-0.5">
            {venue.address.city}, {venue.address.state}
          </p>
        )}
      </Link>
    </InfoWindow>
  );
};
